/**
 * ProcureAI Central Pagination & Query Engine
 * Provides reusable offset/cursor pagination, search, filtering, and sorting 
 * across full enterprise datasets (100+ / 500+ / 1,000+ records).
 */

export interface PaginatedResponse<T> {
  data: T[];
  items?: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface QueryParameters<T = Record<string, unknown>> {
  page?: number;
  pageSize?: number;
  search?: string;
  searchFields?: Array<keyof T>;
  filters?: Partial<Record<keyof T | string, unknown>>;
  sortField?: keyof T | string;
  sortOrder?: 'asc' | 'desc';
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function processQuery<T extends Record<string, any>>(
  records: T[],
  params: QueryParameters<T> = {}
): PaginatedResponse<T> {
  const page = Math.max(1, params.page || 1);
  const pageSize = Math.max(1, Math.min(200, params.pageSize || 25));
  const search = (params.search || '').trim().toLowerCase();

  let filtered = [...records];

  // 1. Text Search across full dataset
  if (search) {
    // Exact reference number match takes highest priority (e.g. "REQ-1005", "1005", "req1005")
    const cleanSearch = search.replace(/[^a-z0-9]/g, '');
    const exactRefMatch = filtered.filter((item) => {
      const refNum = String(item.requestNumber || item.poNumber || item.invoiceNumber || item.referenceNumber || item.id || '').toLowerCase();
      const cleanRef = refNum.replace(/[^a-z0-9]/g, '');
      const numOnly = cleanRef.replace(/^[a-z]+/, ''); // extracts "1005" from "req1005"
      return (
        refNum === search ||
        refNum === `req-${search}` ||
        cleanRef === cleanSearch ||
        cleanRef === `req${cleanSearch}` ||
        (cleanSearch.length >= 3 && numOnly === cleanSearch)
      );
    });

    if (exactRefMatch.length > 0) {
      filtered = exactRefMatch;
    } else {
      filtered = filtered.filter((item) => {
        if (params.searchFields && params.searchFields.length > 0) {
          return params.searchFields.some((field) => {
            const val = item[field];
            return val !== undefined && val !== null && String(val).toLowerCase().includes(search);
          });
        }
      // Default: Deep search across all string / numeric properties
      return Object.values(item).some((val) => {
        if (val === null || val === undefined) return false;
        if (typeof val === 'string' || typeof val === 'number') {
          return String(val).toLowerCase().includes(search);
        }
        if (typeof val === 'object' && !Array.isArray(val)) {
          return Object.values(val).some(
            (nested) => nested !== null && String(nested).toLowerCase().includes(search)
          );
        }
        return false;
      });
    });
    }
  }

  // 2. Structured Filtering across full dataset
  if (params.filters) {
    Object.entries(params.filters).forEach(([key, filterVal]) => {
      if (filterVal === undefined || filterVal === null || filterVal === '' || filterVal === 'ALL') {
        return;
      }

      filtered = filtered.filter((item) => {
        const itemVal = item[key];
        if (Array.isArray(filterVal)) {
          return filterVal.includes(itemVal);
        }
        if (typeof filterVal === 'string' && typeof itemVal === 'string') {
          return itemVal.toLowerCase() === filterVal.toLowerCase();
        }
        return itemVal === filterVal;
      });
    });
  }

  // 3. Sorting applied before pagination
  if (params.sortField) {
    const sortKey = params.sortField as string;
    const isAsc = (params.sortOrder || 'asc') === 'asc';

    filtered.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (aVal === bVal) return 0;
      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const comp = aVal.localeCompare(bVal);
        return isAsc ? comp : -comp;
      }

      const comp = aVal < bVal ? -1 : 1;
      return isAsc ? comp : -comp;
    });
  }

  // 4. Pagination Slicing
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const paginatedData = filtered.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    items: paginatedData,
    page: currentPage,
    pageSize,
    total,
    totalPages,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
  };
}

/**
 * Direct ID Lookup — retrieves any record from full mock dataset regardless of list page
 */
export function getEntityById<T extends Record<string, any>>(
  records: T[],
  id: string,
  idField: keyof T = 'id' as keyof T
): T | undefined {
  if (!id) return undefined;
  const targetId = String(id).trim().toLowerCase();
  return records.find((item) => {
    const itemVal = item[idField];
    return itemVal !== undefined && itemVal !== null && String(itemVal).trim().toLowerCase() === targetId;
  });
}
